
import { useState } from "react";
import { CameraInventory } from "@/types/inventory";
import { CameraCard } from "@/components/inventory/CameraCard";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { Camera, ChevronUp, ChevronDown } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";

interface CompatibleCamerasSectionProps {
  compatibleCameras: CameraInventory[];
  onActionComplete: () => void;
}

export function CompatibleCamerasSection({ 
  compatibleCameras, 
  onActionComplete 
}: CompatibleCamerasSectionProps) {
  const [isOpen, setIsOpen] = useState(true);
  const { t } = useTranslation();

  return (
    <Collapsible 
      open={isOpen} 
      onOpenChange={setIsOpen}
      className="border rounded-lg overflow-hidden mb-6"
    >
      <div className="bg-card p-4 flex items-center justify-between border-b">
        <div>
          <h2 className="text-xl font-semibold">{t('compatible_cameras')}</h2>
          <p className="text-muted-foreground text-sm">
            {t('cameras')} that can use this lens
          </p>
        </div>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
            {isOpen ? 
              <ChevronUp className="h-4 w-4" /> : 
              <ChevronDown className="h-4 w-4" />
            }
          </Button>
        </CollapsibleTrigger>
      </div>
      
      <CollapsibleContent>
        <div className="p-4">
          {compatibleCameras.length === 0 ? (
            <div className="text-center py-12">
              <div className="flex flex-col items-center justify-center space-y-3">
                <Camera className="h-12 w-12 text-muted-foreground opacity-20" />
                <h3 className="font-medium">{t('no_cameras_found')}</h3>
                <p className="text-sm text-muted-foreground">
                  {t('no_cameras_yet')}
                </p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {compatibleCameras.map(camera => (
                <CameraCard 
                  key={camera.id} 
                  camera={camera} 
                  onActionComplete={onActionComplete} 
                />
              ))}
            </div>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
